/**
 * Tax Purpose Adjustments — TZR (Підпорція 3.1b, 2026-05-24).
 *
 * Зміна податкового призначення транспортно-заготівельних витрат.
 * Single-row документ (без items). Одна проводка Дт 6431 — Кт 6442.
 *
 * ПРИХОВАНО з меню/роутів 2026-07-28 (рішення власника): UI без створення
 * (лише список) + рядок 4.1 декларації ПДВ не реалізований. Механіка
 * ст.198.5 ПКУ потрібна лише платникам зі ЗМІШАНОЮ діяльністю — доводимо
 * при першому такому клієнті. Деталі: TECHDEBT «TD-VAT-198-5».
 *
 * 2026-08-22 (Норма #29): серверна пагінація/сортування через
 * InfiniteDocGrid + колонка «Організація» + фільтр за організацією.
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import { tpTzr, type TPTzr } from '@/api/vatAdjustments';
import { orgs } from '@/api/catalogs';
import { useOrgFilter } from '@/store/defaults';
import ImportedBadge from '@/shared/components/ImportedBadge';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';

const fmt = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function TPTzrPage() {
  const navigate = useNavigate();
  // Орг слідує за шапкою до ручного вибору (useOrgFilter).
  const [orgId, setOrgId] = useOrgFilter();
  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const orgName = useMemo(() => {
    const m: Record<string, string> = {};
    (orgsQ.data?.items ?? []).forEach((o) => { m[o.id] = o.name; });
    return m;
  }, [orgsQ.data]);

  const { datasource, total } = useInfiniteGridDatasource<TPTzr>(
    (limit, offset, sort) => tpTzr.list({
      organization_id: orgId || undefined,
      sort_by: sort?.sort_by,
      sort_dir: sort?.sort_dir,
      limit, offset,
    }),
    [orgId],
  );

  const cols = useMemo<ColDef<TPTzr>[]>(() => [
    {
      field: 'document_number', headerName: '№', width: 150,
      cellRenderer: (p: { data?: TPTzr }) => p.data ? (
        <span>
          <ImportedBadge source={p.data.external_source} />
          {p.data.external_source ? ' ' : ''}{p.data.document_number}
        </span>
      ) : '',
    },
    {
      field: 'document_date', headerName: 'Дата', width: 170,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString('uk-UA') : '',
    },
    {
      field: 'organization_id', headerName: 'Організація', width: 200,
      valueFormatter: (p) => p.value ? (orgName[p.value] ?? '') : '',
    },
    { field: 'adjustment_reason', headerName: 'Причина', flex: 1, minWidth: 200 },
    {
      field: 'tzr_amount', headerName: 'Сума ТЗР', width: 140,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value != null ? fmt(p.value) : '',
    },
    {
      field: 'vat_amount_to_compensate', headerName: 'ПДВ компенсація', width: 160,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value != null ? fmt(p.value) : '',
    },
    {
      field: 'posted', headerName: 'Проведено', width: 120,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (p) => p.data ? (p.value ? '✓ так' : 'ні') : '',
    },
  ], [orgName]);

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          Зміна податкового призначення (ТЗР)<TitleTotal total={total} />
        </h2>
        <button
          className="primary"
          onClick={() => navigate('/taxation/tax-purpose-adj-tzr/new')}
        >
          + Новий
        </button>
      </div>
      <div className="filters" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', margin: '0 0 0.6rem', alignItems: 'center' }}>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)}
          style={{ maxWidth: 240 }}>
          <option value="">Усі організації</option>
          {(orgsQ.data?.items ?? []).map((o) => (
            <option key={o.id} value={o.id}>{o.name}</option>
          ))}
        </select>
      </div>
      <InfiniteDocGrid<TPTzr>
        deleteRow={{ remove: (id) => tpTzr.remove(id) }}
        datasource={datasource}
        columnDefs={cols}
        storageKey="tp-tzr"
        onRowDoubleClicked={(r) => navigate(`/taxation/tax-purpose-adj-tzr/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : undefined}
      />
    </div>
  );
}
